/** Mirror of `socaity_schemas.platform.ai_model`. */

import { Organization, ScoreSource } from './organization';

export type Modality = 'text' | 'image' | 'audio' | 'video' | '3d' | 'vector' | 'other';

export class ModelLicense {
  license_type: string | null;
  url: string | null;
  commercial_use: boolean | null;
  restrictions: string | null;

  constructor(data: Record<string, any> = {}) {
    this.license_type = data.license_type ?? null;
    this.url = data.url ?? null;
    this.commercial_use = data.commercial_use ?? null;
    this.restrictions = data.restrictions ?? null;
  }
}

export class ModelArtifact {
  parameter_count: number | null;
  size_mb: number | null;
  runtime_memory_mb: number | null;
  min_gpu_vram_gb: number | null;
  quantization: string | null;
  weights_format: string | null;

  constructor(data: Record<string, any> = {}) {
    this.parameter_count = data.parameter_count != null ? Number(data.parameter_count) : null;
    this.size_mb = data.size_mb != null ? Number(data.size_mb) : null;
    this.runtime_memory_mb = data.runtime_memory_mb != null ? Number(data.runtime_memory_mb) : null;
    this.min_gpu_vram_gb = data.min_gpu_vram_gb != null ? Number(data.min_gpu_vram_gb) : null;
    this.quantization = data.quantization ?? null;
    this.weights_format = data.weights_format ?? null;
  }
}

export class ModelCapabilities {
  input_modalities: Modality[];
  output_modalities: Modality[];
  tasks: string[];
  tool_calling: boolean;
  structured_output: boolean;
  context_window: number | null;
  max_output_tokens: number | null;

  constructor(data: Record<string, any> = {}) {
    this.input_modalities = Array.isArray(data.input_modalities) ? data.input_modalities : [];
    this.output_modalities = Array.isArray(data.output_modalities) ? data.output_modalities : [];
    this.tasks = Array.isArray(data.tasks) ? data.tasks : [];
    this.tool_calling = data.tool_calling === true;
    this.structured_output = data.structured_output === true;
    this.context_window = data.context_window != null ? Number(data.context_window) : null;
    this.max_output_tokens = data.max_output_tokens != null ? Number(data.max_output_tokens) : null;
  }
}

export class ModelScore {
  source: ScoreSource | Record<string, any> | null;
  benchmark: string;
  score: number;
  max_score: number | null;
  url: string | null;
  evaluated_at: string | null;

  constructor(data: Record<string, any> = {}) {
    this.source = data.source
      ? (data.source instanceof ScoreSource ? data.source : new ScoreSource(data.source))
      : null;
    this.benchmark = data.benchmark ?? '';
    this.score = Number(data.score) || 0;
    this.max_score = data.max_score != null ? Number(data.max_score) : null;
    this.url = data.url ?? null;
    this.evaluated_at = data.evaluated_at ?? null;
  }
}

export class Publication {
  paper_title: string | null;
  paper_url: string | null;
  arxiv_id: string | null;
  citation_count: number | null;

  constructor(data: Record<string, any> = {}) {
    this.paper_title = data.paper_title ?? null;
    this.paper_url = data.paper_url ?? null;
    this.arxiv_id = data.arxiv_id ?? null;
    this.citation_count = data.citation_count != null ? Number(data.citation_count) : null;
  }
}

export class AIModel {
  id: string | null;
  name: string | null;
  display_name: string | null;
  description: string | null;
  description_agent: string | null;
  description_agent_updated_at: string | null;
  family: string | null;
  base_model: string | null;
  release_date: string | null;
  organization: Organization | Record<string, any> | null;
  hugging_face_url: string | null;
  is_gated: boolean | null;
  artifact: ModelArtifact | Record<string, any> | null;
  capabilities: ModelCapabilities | Record<string, any> | null;
  license: ModelLicense | Record<string, any> | null;
  scores: ModelScore[];
  publication: Publication | Record<string, any> | null;
  created_at: string | null;
  updated_at: string | null;

  constructor(data: Record<string, any> = {}) {
    this.id = data.id ?? null;
    this.name = data.name ?? null;
    this.display_name = data.display_name ?? null;
    this.description = data.description ?? null;
    this.description_agent = data.description_agent ?? null;
    this.description_agent_updated_at = data.description_agent_updated_at ?? null;
    this.family = data.family ?? null;
    this.base_model = data.base_model ?? null;
    this.release_date = data.release_date ?? null;
    this.organization = data.organization
      ? (data.organization instanceof Organization ? data.organization : new Organization(data.organization))
      : null;
    this.hugging_face_url = data.hugging_face_url ?? null;
    this.is_gated = data.is_gated ?? null;
    this.artifact = data.artifact
      ? (data.artifact instanceof ModelArtifact ? data.artifact : new ModelArtifact(data.artifact))
      : null;
    this.capabilities = data.capabilities
      ? (data.capabilities instanceof ModelCapabilities ? data.capabilities : new ModelCapabilities(data.capabilities))
      : null;
    this.license = data.license
      ? (data.license instanceof ModelLicense ? data.license : new ModelLicense(data.license))
      : null;
    this.scores = Array.isArray(data.scores) ? data.scores.map((s: any) => new ModelScore(s)) : [];
    this.publication = data.publication
      ? (data.publication instanceof Publication ? data.publication : new Publication(data.publication))
      : null;
    this.created_at = data.created_at ?? null;
    this.updated_at = data.updated_at ?? null;
  }
}
